//Dependencies
import { Fragment, useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useIntl } from "react-intl";

//Controls
import { Button } from "@gpstrackit/react-controls";

import { getDateFormatted, getMinDateLimitBehaviour, getQuery, getPMUser, getTransactionsProcessingStatusColor } from 'Core/data/Helpers';
import DataView from 'Components/DataView';

import { Chip } from "@mui/material";
import { NotificationManager } from "Components/Notifications";

import InputDate from 'Components/InputDate';
import moment from 'moment';

import SendCommand from 'Modules/devices/.components/sendCommand';
import Filters from 'Components/Filters';

//Redux
import {
    messagesOutboxTabGetData,
    sendCommandOpenModal,
    searchEntityRequest
} from "Redux/actions";

//Permission
import { HasPermission } from "Core/security"

const MessagesOutbox = (props) => {
    const { height, className } = props
    const tableRef = useRef();
    const { messages } = useIntl();
    const dispatch = useDispatch();

    //redux states
    const { loading, data, nextToken, conditions } = useSelector(state => state.messagesOutboxRedux);
    const pmUsers = useSelector(state => state.generalRedux.pmUsers);
    const user = useSelector(state => state.securityRedux.user);
    const sendCommandsOpenModal = useSelector(state => state.sendCommandRedux.openModal);
    const massSMS = useSelector(state => state.sendCommandRedux.massSMS);

    const current_week = moment().startOf('week');
    const tempStartDate = new Date(current_week.toString());
    const [startDate, setStartDate] = useState(tempStartDate);
    const [endDate, setEndDate] = useState(new Date());
    const [beforeRangeDate, setBeforeRangeDate] = useState({
        start: tempStartDate,
        end: endDate
    })
    const [fromTab, setFromTab] = useState(false);
    const [filters, setFilters] = useState({
        startDate: tempStartDate,
        endDate: endDate,
        filterByDate: true,
        limit: 50
    })
    const [last, setLast] = useState({ id: '20', name: '20' });
    const [type, setType] = useState('last');

    const [columns, setColumns] = useState([
        {
            key: 'createdOn',
            show: true,
            title: messages["message_outbox_tab_created_on"],
            width: 200,
            field: "createdOn",
            export: true,
            resize: true,
            functionString:
                `
            function formatDate(date) {
                let dateToformat = moment(date)
                let formatted = moment(dateToformat).format("YYYY/MM/DD HH:mm:ss")
                return formatted;
            }
            return row?.createdOn ? formatDate(row?.createdOn) : ''
            `,
            code: "message_outbox_tab_created_on",
            render: (dataItem) => {
                return (
                    <Fragment>
                        {
                            dataItem.createdOn &&
                            getDateFormatted(dataItem.createdOn, "date", "datetime")
                        }
                    </Fragment>
                )
            }
        },
        {
            key: 'via',
            show: true,
            title: messages["message_outbox_tab_via"],
            width: 100,
            field: "via",
            export: true,
            resize: true,
            functionString: 'return row?.via',
            code: "message_outbox_tab_via",
            render: (item) => {
                let via = ''
                if (item.via === 'SMS')
                    via = 'SMS OUT'
                else if (item.via === 'SMS_MO_RECEIVED')
                    via = 'SMS IN'
                else
                    via = item.via

                return <span>{via}</span>
            }
        },
        {
            key: 'status',
            show: true,
            title: messages["message_outbox_tab_status"],
            width: 100,
            field: "via",
            export: true,
            resize: true,
            functionString: 'return row?.status',
            code: "message_outbox_tab_status",
            render: (item) => {
                return <Chip style={{
                    backgroundColor: getTransactionsProcessingStatusColor(item?.status).color,
                    color: "#ffffff"
                }} label={messages[getTransactionsProcessingStatusColor(item?.status).label]} />
            }
        },
        {
            key: 'description',
            show: true,
            title: messages["message_outbox_tab_description"],
            width: 200,
            field: "description",
            export: true,
            resize: true,
            functionString: 'return row?.description',
            code: "message_outbox_tab_description",
        },
        {
            key: 'message',
            show: true,
            title: messages["message_outbox_tab_message"],
            width: 200,
            field: "message",
            export: true,
            resize: true,
            functionString: 'return row?.message',
            code: "message_outbox_tab_message",
        },
        {
            key: 'to',
            show: true,
            title: messages["message_outbox_tab_to"],
            width: 200,
            field: "to",
            export: true,
            resize: true,
            functionString: 'return row?.to',
            code: "message_outbox_tab_to",
        },
        {
            key: 'createdBy',
            show: true,
            title: messages["message_outbox_tab_created_by"],
            width: 200,
            field: "createdBy",
            export: true,
            resize: true,
            functionString: `
                let pmUsers = ${JSON.stringify(pmUsers)}
                pmUsers = pmUsers.find((item) => ("PM_"+item.id) === row?.createdBy)
                return pmUsers.firstName + " " + pmUsers.lastName || row?.createdBy
            `,
            code: "message_outbox_tab_created_by",
            render: (item) => {
                return <span>{item?.createdBy && getPMUser(item?.createdBy, pmUsers)}</span>
            }
        }
    ])

    const [columnsShort, setColumnsShort] = useState([
        {
            key: 'createdOn',
            show: true,
            title: messages["message_outbox_tab_created_on"],
            width: 200,
            field: "createdOn",
            export: true,
            resize: true,
            functionString:
                `
            function formatDate(date) {
                let dateToformat = moment(date)
                let formatted = moment(dateToformat).format("YYYY/MM/DD HH:mm:ss")
                return formatted;
            }
            return row?.createdOn ? formatDate(row?.createdOn) : ''
            `,
            code: "message_outbox_tab_created_on",
            render: (dataItem) => {
                return (
                    <Fragment>
                        {
                            dataItem.createdOn &&
                            getDateFormatted(dataItem.createdOn, "date", "datetime")
                        }
                    </Fragment>
                )
            }
        },
        {
            key: 'message',
            show: true,
            title: messages["message_outbox_tab_message"],
            width: 200,
            field: "message",
            export: true,
            resize: true,
            functionString: 'return row?.message',
            code: "message_outbox_tab_message",
        },
        {
            key: 'via',
            show: true,
            title: messages["message_outbox_tab_via"],
            width: 100,
            field: "via",
            export: true,
            resize: true,
            functionString: 'return row?.via',
            code: "message_outbox_tab_via",
        }
    ])
    const [device, setDevice] = useState(props.device);
    const [short, setShort] = useState(props.short || false);

    const loadInitData = (sugestFilters, paggingFilters) => {
        let currentFilters = filters

        if (sugestFilters)
            currentFilters = sugestFilters;

        setFilters({ ...currentFilters })

        currentFilters.last = last
        currentFilters.type = type

        tableRef?.current?.conditionsHandler(currentFilters);

        dispatch(messagesOutboxTabGetData(device.id, currentFilters, nextToken));
    }

    const onChangeStartEndDate = ({ start, end }) => {
        const newStart = start && new Date(start);
        const newEnd = end && new Date(end);

        let fltrs = filters;
        fltrs.startDate = newStart;
        fltrs.endDate = newEnd;
        // Set New Date Range
        if (start && end) {
            const newDateRange = {
                start,
                end,
            };
            setBeforeRangeDate(newDateRange)
        }
        setFilters({ ...fltrs });
    }

    const onCloseSelectDate = () => {
        const { start, end } = beforeRangeDate;
        if (start && end && !endDate) {
            let fltrs = filters;
            fltrs.startDate = start;
            fltrs.endDate = end;
            setFilters({ ...fltrs });
        }
    }

    const onChange = (value) => {
        const { start, end } = value;
        onChangeStartEndDate({
            start,
            end
        })
    };

    const applyFilters = () => {
        if (filters.startDate && filters.endDate) {
            loadInitData();
        } else {
            NotificationManager.warning(
                messages['message_outbox_tab_date_range'],
                messages['message_outbox_tab_title'],
                5000,
            );
        }
    }

    useEffect(() => {
        (!pmUsers || pmUsers.length === 0) && dispatch(searchEntityRequest({ key: "pmUsers" }));
        loadInitData();
    }, []);

    useEffect(() => {
        if (pmUsers.length > 0) {
            let currentColumns = columns.map((item) => ({
                ...item,
                title: messages[item.code],
                render: item.key === "CreatedBy" ? (item) => {
                    let label = item?.CreatedBy ? getPMUser(item?.CreatedBy, pmUsers) : ""
                    return <span>{label}</span>
                } : item.render,
                functionString: item.key === "CreatedBy" ? `
                    let pmUsers = ${JSON.stringify(pmUsers)}
                    pmUsers = pmUsers.find((item) => ("PM_"+item.id) === row?.createdBy)
                    return pmUsers.firstName + " " + pmUsers.lastName || row?.createdBy
                ` : item.functionString
            }))
            setColumns([...currentColumns])
        }
    }, [pmUsers])

    useEffect(() => {
        if (data && data.error) {
            let message = messages["message_outbox_tab_error_load"]
            if (data?.error?.error) {
                if (messages[data.error.error])
                    message = messages[data.error.error]
                else
                    message = data.error.error.replace("Bad request ->", "").replace("GraphQL error: ", "")
            }

            NotificationManager.error(message, messages['message_outbox_tab_title'], 6000, null, null, '', null);
        }
    }, [data]);

    return (
        <Fragment>
            <DataView
                ref={tableRef}
                exportName="commands"
                columns={short ? columnsShort : columns}
                name={short ? "pm-commands-tab-outbox-short" : "pm-commands-tab-outbox"}
                height={height && height + "px" || "600px"}
                noSearch
                noFilters
                externalRefresh={(paggingFilters) => { loadInitData(null, paggingFilters) }}
                messages={messages}
                externalLoading={loading}
                externalData={data || []}
                noSplit
                infiniteScroll={type !== 'last'}
                hidePagination
                disableItemSelection
                extraFilters={() => {
                    return (
                        <div style={{ width: "100%", display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>

                            <Filters

                                onChangeFilters={() => applyFilters()}
                                startDate={startDate}
                                endDate={endDate}
                                setStartDate={setStartDate}
                                setEndDate={setEndDate}
                                setBeforeRangeDate={setBeforeRangeDate}
                                beforeRangeDate={beforeRangeDate}
                                onChange={onChange}
                                onChangeStartEndDate={onChangeStartEndDate}
                                last={last}
                                setLast={setLast}
                                type={type}
                                setType={setType}
                            />
                            {
                                HasPermission(user, "sndcom") && !short &&
                                <div style={{ paddingTop: 15, marginLeft: 10 }}>
                                    <Button
                                        className="btn btn-dark-blue btn-block"
                                        size="sm"
                                        style={{ height: 42 }}
                                        onClick={() => {
                                            setFromTab(true)
                                            dispatch(sendCommandOpenModal({ selectedDevices: [device] }))
                                        }}
                                        disabled={props.loading}
                                    >
                                        {messages['message_outbox_tab_send_command']}
                                    </Button>
                                </div>
                            }
                        </div>

                    )
                }}
                onChangeConditions={(newTags, newConditions, total, newColumns, rows, sort) => {
                    
                }}
                noExcel
                onChangeColumns={(items) => short ? setColumnsShort(items) : setColumns(items)}
                tableButtons={["export", 'orderColumn']}
                hasMore={nextToken ? true : false}
                query={getQuery("commands")}
                conditions={conditions}
                isDynamo
                noPDF
            />
            {sendCommandsOpenModal && fromTab && !massSMS && <SendCommand single setFromTab={setFromTab} loadData={loadInitData} massSMS={false} />}
        </Fragment>
    )

}

export default MessagesOutbox;